/**
 * Utility to detect and convert URLs in text to clickable links
 * Supports http://, https://, and www. URLs
 */

/**
 * Convert URLs in text to React elements with clickable links
 * @param {string} text - Text that may contain URLs
 * @returns {React.ReactNode} - Text with URLs converted to <a> tags
 */
export function parseUrlsInText(text) {
  if (!text || typeof text !== 'string') return text;

  // Regex pattern for URLs: http(s):// or www.
  const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

  const parts = text.split(urlPattern);

  return parts.map((part, index) => {
    if (!part) return null;

    // Check if this part is a URL
    if (urlPattern.test(part)) {
      // Ensure URL has protocol
      let url = part;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + part;
      }

      return (
        <a
          key={index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="note-url-link"
          onClick={(e) => {
            // Prevent event bubbling that might trigger note editing
            e.stopPropagation();
          }}
          title={url}
        >
          {part}
        </a>
      );
    }

    return part;
  }).filter(Boolean);
}

/**
 * Convert URLs in text to clickable links with line breaks preserved
 * @param {string} text - Text that may contain URLs and line breaks
 * @returns {React.ReactNode} - Text with URLs and line breaks handled
 */
export function parseUrlsWithLineBreaks(text) {
  if (!text || typeof text !== 'string') return text;

  // Split by line breaks first
  const lines = text.split('\n');

  return lines.map((line, lineIndex) => {
    const parsedLine = parseUrlsInText(line);

    return (
      <div key={lineIndex} className="note-line">
        {parsedLine}
      </div>
    );
  });
}

/**
 * Extract all URLs from text
 * @param {string} text - Text to search for URLs
 * @returns {Array<string>} - Array of unique URLs found
 */
export function extractUrls(text) {
  if (!text || typeof text !== 'string') return [];

  const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const matches = text.match(urlPattern);

  return matches ? [...new Set(matches)] : [];
}
